import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { errMsg } from '../api';
import { useCart } from '../context/CartContext';
import { formatUGX } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Banknote, Smartphone, ArrowLeft, CheckCircle2, Printer, Loader2, ShoppingCart, AlertTriangle,
} from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { items, customer, subtotal, clear } = useCart();

  const [method, setMethod] = useState('cash');
  const [provider, setProvider] = useState('mtn');
  const [phone, setPhone] = useState('');
  const [tendered, setTendered] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [sale, setSale] = useState(null);

  if (items.length === 0 && !sale) {
    return (
      <div className="flex flex-col items-center justify-center py-28 text-center">
        <div className="h-16 w-16 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><ShoppingCart size={28} /></div>
        <p className="font-display font-bold text-slate-800 text-lg mt-5">Your cart is empty</p>
        <p className="text-slate-500 mt-1">Add products in the POS before checking out.</p>
        <Button onClick={() => navigate('/app/pos')} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-6 font-semibold">Back to POS</Button>
      </div>
    );
  }

  const change = method === 'cash' && tendered ? Number(tendered) - subtotal : 0;
  const canPay = method === 'cash' ? Number(tendered) >= subtotal : phone.trim().length >= 9;

  const completePayment = async () => {
    setProcessing(true);
    setError('');
    try {
      const payload = {
        lines: items.map((i) => ({ product_id: i.id, quantity: i.qty, unit_price: i.price })),
        customer: customer.trim() || 'Walk-in',
        payment_method: method === 'cash' ? 'cash' : provider,
        provider: method === 'momo' ? provider : null,
        customer_phone: method === 'momo' ? phone.trim() : null,
      };
      const res = await api.post('/sales', payload);
      setSale(res.data);
      toast({ title: 'Payment received', description: `${formatUGX(res.data.total)}` });
    } catch (e) {
      // 409 = stock changed / insufficient — reject whole sale
      setError(errMsg(e, 'Payment could not be completed'));
      toast({ title: 'Sale rejected', description: errMsg(e), variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const backToPOS = () => { clear(); navigate('/app/pos'); };

  // ---------- Success ----------
  if (sale) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto"><CheckCircle2 size={34} /></div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-5">Payment successful</h1>
          <p className="text-slate-500 mt-1">{sale.sale_number} · {sale.customer}</p>

          <div className="text-left border-y border-dashed border-slate-200 my-6 py-4 space-y-3">
            {sale.lines.map((l, idx) => (
              <div key={idx}>
                <div className="flex justify-between text-slate-700 text-sm font-medium">
                  <span>{l.product_name} × {l.quantity}</span>
                  <span>{formatUGX(l.line_total)}</span>
                </div>
                <div className="text-slate-400 text-xs mt-0.5">
                  Batches: {l.allocations.map((a) => `${a.batch_no} (${a.qty})`).join(', ')}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-display font-bold text-lg text-slate-900">
            <span>Total paid</span><span>{formatUGX(sale.total)}</span>
          </div>
          <p className="text-slate-500 mt-2">
            via {method === 'cash' ? 'Cash' : (provider === 'mtn' ? 'MTN' : 'Airtel') + ' Mobile Money'}
            {method === 'cash' && Number(tendered) > subtotal ? ` · Change ${formatUGX(change)}` : ''}
          </p>
          <div className="flex gap-3 justify-center mt-7">
            <Button variant="outline" className="gap-2"><Printer size={16} /> Print receipt</Button>
            <Button onClick={backToPOS} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">Back to POS</Button>
          </div>
        </div>
      </div>
    );
  }

  const MethodCard = ({ value, Icon, title, desc }) => (
    <button onClick={() => setMethod(value)}
      className={`flex items-center gap-4 w-full p-5 rounded-2xl border-2 text-left transition-all ${method === value ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${method === value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}><Icon size={24} /></div>
      <div>
        <div className="font-display font-bold text-slate-900">{title}</div>
        <div className="text-slate-500 text-sm">{desc}</div>
      </div>
      <div className={`ml-auto h-5 w-5 rounded-full border-2 flex items-center justify-center ${method === value ? 'border-blue-600' : 'border-slate-300'}`}>
        {method === value && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
      </div>
    </button>
  );

  return (
    <div>
      <button onClick={() => navigate('/app/pos')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium mb-4 transition-colors">
        <ArrowLeft size={16} /> Back to POS
      </button>
      <h1 className="font-display font-extrabold text-4xl text-slate-900">Checkout</h1>
      <p className="text-slate-500 mt-2 text-[15px]">Confirm the order and take payment from the customer.</p>

      {error && (
        <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div><div className="font-semibold">Sale rejected</div><div className="text-sm">{error}</div></div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6 mt-6 items-start">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-display font-bold text-slate-900">Order summary</h3></div>
          <div className="divide-y divide-slate-100">
            {items.map((i) => (
              <div key={i.id} className="flex items-center justify-between px-5 py-3">
                <div><div className="font-medium text-slate-800 text-sm">{i.name}</div><div className="text-slate-400 text-xs">{formatUGX(i.price)} × {i.qty}</div></div>
                <div className="font-semibold text-slate-800 text-sm">{formatUGX(i.price * i.qty)}</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-slate-500">Total due</span>
            <span className="font-display font-extrabold text-2xl text-slate-900">{formatUGX(subtotal)}</span>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="space-y-3">
            <MethodCard value="cash" Icon={Banknote} title="Cash" desc="Customer pays with cash" />
            <MethodCard value="momo" Icon={Smartphone} title="Mobile Money" desc="MTN or Airtel Money" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            {method === 'cash' ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700">Amount tendered (UGX)</Label>
                  <Input type="number" value={tendered} onChange={(e) => setTendered(e.target.value)} placeholder={String(subtotal)} className="mt-1.5 h-12 rounded-lg text-lg" />
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                  <span className="text-slate-500">Change due</span>
                  <span className={`font-display font-bold text-lg ${change < 0 ? 'text-red-500' : 'text-green-600'}`}>{formatUGX(Math.max(0, change))}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 mb-2 block">Provider</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ v: 'mtn', l: 'MTN Mobile Money', c: 'bg-yellow-400' }, { v: 'airtel', l: 'Airtel Money', c: 'bg-red-500' }].map((p) => (
                      <button key={p.v} onClick={() => setProvider(p.v)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${provider === p.v ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300'}`}>
                        <span className={`h-8 w-8 rounded-lg ${p.c} flex items-center justify-center text-white text-xs font-bold`}>{p.v === 'mtn' ? 'MTN' : 'A'}</span>
                        <span className="font-medium text-slate-800 text-sm">{p.l}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700">Customer phone number</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" className="mt-1.5 h-12 rounded-lg text-lg" />
                  <p className="text-slate-400 text-xs mt-1.5">A payment prompt will be sent to this number.</p>
                </div>
              </div>
            )}
          </div>

          <Button onClick={completePayment} disabled={!canPay || processing}
            className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-[17px] gap-2">
            {processing ? <><Loader2 className="animate-spin" size={20} /> Processing…</> : <>Complete payment · {formatUGX(subtotal)}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
